import { AuthService } from './../../../services/auth.service';
import { authGuard } from './../../../core/guards/auth.guard';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { AppConfigurator } from '../app.configurator';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../../service/layout.service';
import { environment } from '../../../../environments/environment';
import { ProfileService } from '../../../services/profile.service';
import { SettingsService } from '../../../services/settings.service';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { CompanyService } from '../../../services/company.service';
import { CompanyCompactDto, CompanyDto } from '../../../shared/models/company.model';
import { Subscription, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [RouterModule, CommonModule, StyleClassModule, AppConfigurator, MenuModule, ToggleSwitchModule, FormsModule],
  templateUrl: './topbar.component.html'
})
export class TopbarComponent implements OnInit, OnDestroy {
  @ViewChild('menu') menu!: Menu;
  @ViewChild('profileButton') profileButton!: ElementRef;

  authService = inject(AuthService);
  router = inject(Router);
  profileService = inject(ProfileService);
  settingsService = inject(SettingsService);
  companyService = inject(CompanyService);

  items!: MenuItem[];
  logoUrl = environment.logoUrl;
  logoUrlDark = environment.logoUrlDark;
  companyName = environment.companyName;
  agencyName = '';
  selectedCompany: CompanyCompactDto | null = null;

  menuItems: MenuItem[] = [
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: () => {
        this.navigateToProfile();
      }
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        this.logout();
      }
    }
  ];

  get cacheEnabled(): boolean {
    return this.settingsService.cacheEnabled;
  }

  set cacheEnabled(value: boolean) {
    this.settingsService.cacheEnabled = value;
  }

  isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  constructor(public layoutService: LayoutService) {}

  private profileSubscription: Subscription | undefined;
  private selectedCompanySubscription: Subscription | undefined;

  ngOnInit(): void {
    if (this.isSuperAdmin()) {
      this.menuItems = this.menuItems.filter((item) => item.label !== 'Profile');
    }
    // Subscribe to selected company changes for Super Admin
    this.selectedCompanySubscription = this.companyService
      .getSelectedCompanyCompact()
      .pipe(distinctUntilChanged())
      .subscribe((company) => {
        this.selectedCompany = company;
      });
    this.profileSubscription = this.profileService.getProfile().subscribe((profile) => {
      if (profile) {
        this.agencyName = profile.companyName;
      }
    });
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme
    }));
  }

  navigateToProfile(): void {
    this.router.navigate(['/profile']);
  }

  logout(): void {
    this.router.navigate(['/auth/login']);
    this.authService.logout().subscribe(() => {});
  }

  toggleMenu(event: Event): void {
    this.menu.toggle(event);
  }

  onMenuShow(): void {
    // no-op: avoid locking body scroll; rely on layout to manage mobile menu body locks
  }

  onMenuHide(): void {
    this.clearBodyScrollLocks();
  }

  ngOnDestroy(): void {
    // Ensure any lingering body locks are cleared when the component is destroyed
    this.clearBodyScrollLocks();
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
    if (this.selectedCompanySubscription) {
      this.selectedCompanySubscription.unsubscribe();
    }
  }

  private clearBodyScrollLocks(): void {
    if (document?.body?.classList) {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('blocked-scroll');
    }
  }
}
